/**
 * DeviceMismatchModal - HU-FE-04 / HU-FE-11
 * Modal que se muestra cuando el usuario intenta ingresar
 * desde un dispositivo diferente al vinculado.
 * Sprint 5: Incluye opción de desvincular dispositivo
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../ui/Modal';
import { Button, Alert, Input } from '../ui';
import { otpService, savePendingEmail } from '../../services/otp';

interface DeviceMismatchModalProps {
    /** Si el modal está visible */
    isOpen: boolean;
    /** Callback al cerrar/aceptar */
    onClose: () => void;
    /** Email del usuario (para desvinculación) */
    email?: string;
    /** Nombre del dispositivo vinculado (opcional, para mostrar info) */
    deviceName?: string;
    /** Email de contacto de soporte */
    supportEmail?: string;
}

export function DeviceMismatchModal({
    isOpen,
    onClose,
    email,
    deviceName,
    supportEmail = 'soporte@marketplace.com',
}: DeviceMismatchModalProps) {
    const navigate = useNavigate();

    // Sprint 5: Estado para flujo de desvinculación
    const [showUnbindForm, setShowUnbindForm] = useState(false);
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Iniciar proceso de desvinculación
    const handleRequestUnbind = useCallback(async () => {
        if (!email || !password) {
            setError('Ingresa tu contraseña para continuar');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await otpService.requestUnbind(email, password);

            // Guardar email y navegar a página de desvinculación
            savePendingEmail(email);
            onClose();
            navigate('/device-unbind', { state: { email } });
        } catch (err: unknown) {
            const apiError = err as { error_code?: string; message?: string };

            if (apiError.error_code === 'SAME_DEVICE') {
                setError('Ya estás en el dispositivo vinculado.');
            } else if (apiError.error_code === 'NO_DEVICE_BOUND') {
                setError('No tienes ningún dispositivo vinculado.');
            } else {
                setError(apiError.message || 'Error al solicitar desvinculación');
            }
        } finally {
            setIsLoading(false);
        }
    }, [email, password, navigate, onClose]);

    // Reset al cerrar
    const handleClose = useCallback(() => {
        setShowUnbindForm(false);
        setPassword('');
        setError(null);
        onClose();
    }, [onClose]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Dispositivo no autorizado"
            size="medium"
            closeOnBackdrop={false}
            closeOnEscape={false}
        >
            <div className="flex flex-col items-center gap-5 text-center">
                {/* Icono de advertencia */}
                <div
                    className="flex h-20 w-20 items-center justify-center rounded-full bg-error-bg text-error"
                    aria-hidden="true"
                >
                    <svg
                        width="64"
                        height="64"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                        <line x1="12" y1="18" x2="12.01" y2="18" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                    </svg>
                </div>

                <Alert type="error" className="w-full">
                    Tu cuenta ya está vinculada a otro dispositivo.
                </Alert>

                {/* Vista principal */}
                {!showUnbindForm && (
                    <>
                        <div className="flex flex-col gap-3 text-[0.9375rem] leading-6 text-text-secondary">
                            <p>
                                Por razones de seguridad, cada cuenta solo puede utilizarse
                                en un dispositivo a la vez.
                            </p>

                            {deviceName && (
                                <p className="rounded-lg bg-hover px-4 py-3 text-sm">
                                    <strong>Dispositivo actual:</strong> {deviceName}
                                </p>
                            )}

                            <p className="text-sm text-text-muted">
                                Puedes desvincular tu dispositivo anterior para usar este,
                                o contactar a soporte si crees que es un error.
                            </p>
                        </div>

                        <div className="mt-2 flex w-full flex-col gap-3">
                            {/* Sprint 5: Botón de desvincular */}
                            {email && (
                                <Button
                                    variant="primary"
                                    size="large"
                                    fullWidth
                                    onClick={() => setShowUnbindForm(true)}
                                >
                                    Desvincular dispositivo anterior
                                </Button>
                            )}

                            <Button
                                variant="secondary"
                                size="large"
                                fullWidth
                                onClick={() => {
                                    window.location.href = `mailto:${supportEmail}?subject=Solicitud%20de%20cambio%20de%20dispositivo`;
                                }}
                            >
                                Contactar soporte
                            </Button>

                            <Button
                                variant="ghost"
                                size="large"
                                fullWidth
                                onClick={handleClose}
                            >
                                Cancelar
                            </Button>
                        </div>
                    </>
                )}

                {/* Sprint 5: Formulario de desvinculación */}
                {showUnbindForm && (
                    <>
                        <div className="flex w-full flex-col gap-4 text-left">
                            <p>
                                Ingresa tu contraseña para confirmar. Te enviaremos un código
                                de verificación a tu correo.
                            </p>

                            {error && (
                                <Alert type="error" className="w-full">
                                    {error}
                                </Alert>
                            )}

                            <Input
                                label="Contraseña"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Tu contraseña actual"
                                autoComplete="current-password"
                                disabled={isLoading}
                            />
                        </div>

                        <div className="mt-2 flex w-full flex-col gap-3">
                            <Button
                                variant="primary"
                                size="large"
                                fullWidth
                                onClick={handleRequestUnbind}
                                isLoading={isLoading}
                                disabled={!password}
                            >
                                Enviar código de verificación
                            </Button>

                            <Button
                                variant="ghost"
                                size="large"
                                fullWidth
                                onClick={() => {
                                    setShowUnbindForm(false);
                                    setError(null);
                                    setPassword('');
                                }}
                                disabled={isLoading}
                            >
                                Volver
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}
